/**
 * Phase 6.2.2 — Customer Data Quality dispatcher page.
 *
 * Read-only audit + inline fix-up for customer records flagged by the field
 * quality validator. Super-admin only (regional dispatchers can request a
 * promotion later if needed).
 *
 * Surface routes:
 *   /dispatch/admin/data-quality           — list view
 *   /dispatch/admin/data-quality?user=ID   — opens the row pre-expanded
 */
import { useCallback, useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';
import { ArrowLeft, ArrowUpRight, RefreshCw, AlertTriangle, AlertCircle, CheckCircle2, ChevronDown, Mail, Bell, Send } from 'lucide-react';
import { Navbar } from '../../components/Navbar';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const sevBadgeClass = {
  critical: 'text-red-400 border-red-400/50 bg-red-400/10',
  warning: 'text-amber-400 border-amber-400/50 bg-amber-400/10',
  info: 'text-cyan-400 border-cyan-400/50 bg-cyan-400/10',
};

const SeverityBadge = ({ sev }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 border font-mono text-[10px] uppercase tracking-wider ${sevBadgeClass[sev] || sevBadgeClass.info}`}>
    {sev === 'critical' ? <AlertTriangle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
    {sev}
  </span>
);

export default function DataQualityPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusUserId = searchParams.get('user');
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all | critical | warning
  const [regionFilter, setRegionFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [expanded, setExpanded] = useState(focusUserId || null);
  const [editing, setEditing] = useState({}); // {userId: {name, phone, address}}
  const [notifyingId, setNotifyingId] = useState(null); // userId currently sending
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/data-quality/audit`, { withCredentials: true });
      setAudit(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load audit');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  const rows = useMemo(() => {
    if (!audit) return [];
    let list = audit.flagged_users;
    if (filter !== 'all') list = list.filter((u) => (u.summary?.[filter] || 0) > 0);
    if (regionFilter !== 'all') {
      list = list.filter((u) => (u.region || '__none__') === regionFilter);
    }
    if (roleFilter !== 'all') {
      list = list.filter((u) => (u.role || 'customer') === roleFilter);
    }
    return list;
  }, [audit, filter, regionFilter, roleFilter]);

  // Derive distinct regions + roles from the audit response so the dropdowns
  // auto-adapt to whatever's actually in the data (no hard-coded lists).
  const { regions, roles } = useMemo(() => {
    if (!audit) return { regions: [], roles: [] };
    const r = new Set();
    const ro = new Set();
    for (const u of audit.flagged_users) {
      r.add(u.region || '__none__');
      ro.add(u.role || 'customer');
    }
    return {
      regions: Array.from(r).sort(),
      roles: Array.from(ro).sort(),
    };
  }, [audit]);

  const handleSaveFix = async (userId) => {
    const edits = editing[userId] || {};
    const payload = { user_id: userId };
    if (edits.name !== undefined) payload.name = edits.name;
    if (edits.phone !== undefined) payload.phone = edits.phone;
    if (edits.address !== undefined) payload.address = edits.address;
    try {
      const r = await axios.post(`${API_URL}/api/admin/data-quality/fix`, payload, { withCredentials: true });
      toast.success(`Saved — ${r.data.summary.total} issue(s) remain`);
      setEditing((prev) => { const next = { ...prev }; delete next[userId]; return next; });
      fetchAudit();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const handleRecheck = async (userId) => {
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/data-quality/recheck`,
        { user_id: userId },
        { withCredentials: true },
      );
      toast.success(`Re-checked — ${r.data.summary.total} issue(s)`);
      fetchAudit();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Recheck failed');
    }
  };

  // Feb 2026 — send templated 'fix your profile' nudge to one customer
  const handleNotify = async (userId, channels) => {
    setNotifyingId(`${userId}:${channels.join(',')}`);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/data-quality/notify`,
        { user_id: userId, channels },
        { withCredentials: true },
      );
      const bits = [];
      if (r.data.email_sent) bits.push('email ✓');
      if (r.data.push_sent) bits.push(`push ✓ (${r.data.push_result?.sent || 0})`);
      if (r.data.email_error) bits.push(`email ✗ ${r.data.email_error}`);
      if (r.data.push_error) bits.push(`push ✗ ${r.data.push_error}`);
      toast.success(`Notified — ${bits.join(', ') || 'no channels reached'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Notify failed');
    } finally {
      setNotifyingId(null);
    }
  };

  // Bulk-notify ALL flagged customers (customer-role only on the backend)
  const handleBulkNotify = async (channels) => {
    setBulkSending(true);
    setBulkConfirmOpen(false);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/data-quality/notify-bulk`,
        { channels },
        { withCredentials: true, timeout: 120000 },
      );
      toast.success(
        `Bulk notify done: ${r.data.sent_email} emails, ${r.data.sent_push} pushes · ${r.data.skipped} skipped (dedup) · ${r.data.failed} failed`,
        { duration: 8000 },
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk notify failed');
    } finally {
      setBulkSending(false);
    }
  };

  const handleSendTestBrief = async () => {
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/brief/test-send`,
        {},
        { withCredentials: true },
      );
      if (r.data?.skipped) {
        toast.warning(`Brief skipped: ${r.data.reason}`);
      } else {
        toast.success(`Brief sent to ${r.data.sent_count} super-admin(s)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    }
  };

  return (
    <div className="min-h-screen bg-[#050003] scanline-overlay dispatch-safe-top">
      <Navbar />
      <div className="max-w-6xl mx-auto p-4 sm:p-8">
        <button
          onClick={() => navigate(-1)}
          className="text-[#A3C7A8] hover:text-[#39FF14] font-mono text-xs uppercase tracking-wider flex items-center gap-2 mb-4"
          data-testid="dq-back-button"
        >
          <ArrowLeft className="w-4 h-4" /> // BACK
        </button>

        <div className="border border-[#2A0F3D] bg-[#0C0612] p-6 mb-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-[#39FF14] font-mono text-xl font-bold uppercase tracking-wider">
                {'>'} DATA_QUALITY_AUDIT_
              </h1>
              <p className="text-[#A3C7A8] font-mono text-xs mt-2">
                // Customer records flagged by the field-quality validator.
                Critical = will block ordering. Warning = may cause driver pain.
              </p>
              {/* Feb 2026 — Sibling page nav so the "[object Object]" cohort
                  (surfaced on AT-RISK CUSTOMERS) is one click away from the
                  main data-quality workspace. Previously only reachable by
                  typing the URL manually. */}
              <Link
                to="/dispatch/admin/at-risk-customers"
                className="inline-flex items-center gap-1.5 mt-3 text-[#D4823B] hover:text-[#39FF14] font-mono text-xs uppercase tracking-wider border border-[#D4823B]/50 hover:border-[#39FF14] px-2.5 py-1"
                data-testid="dq-goto-at-risk"
              >
                <AlertTriangle className="w-3 h-3" />
                {'// AT-RISK CUSTOMERS →'}
              </Link>
            </div>
            <div className="flex flex-col gap-2 items-stretch sm:items-end">
              <Button
                onClick={handleSendTestBrief}
                variant="outline"
                className="border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black font-mono text-xs uppercase tracking-wider rounded-none whitespace-nowrap"
                data-testid="dq-send-test-brief"
              >
                ▶ Send test brief now
              </Button>
              <Button
                onClick={() => setBulkConfirmOpen(true)}
                disabled={bulkSending}
                variant="outline"
                className="border-[#FF9F0A] text-[#FF9F0A] hover:bg-[#FF9F0A] hover:text-black font-mono text-xs uppercase tracking-wider rounded-none whitespace-nowrap disabled:opacity-50"
                data-testid="dq-bulk-notify"
              >
                <Send className="w-3 h-3 mr-1" />
                {bulkSending ? 'Sending...' : 'Notify all flagged'}
              </Button>
            </div>
          </div>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="border border-[#2A0F3D] bg-[#0C0612] p-4">
            <div className="text-[#6B8E73] font-mono text-[10px] uppercase tracking-widest mb-1">Scanned</div>
            <div className="text-[#39FF14] font-mono text-2xl font-bold">{audit?.scanned ?? '—'}</div>
          </div>
          <div className="border border-[#2A0F3D] bg-[#0C0612] p-4">
            <div className="text-[#6B8E73] font-mono text-[10px] uppercase tracking-widest mb-1">Flagged</div>
            <div className="text-[#FF9F0A] font-mono text-2xl font-bold">{audit?.flagged_count ?? '—'}</div>
          </div>
          <div className="border border-[#2A0F3D] bg-[#0C0612] p-4">
            <div className="text-[#6B8E73] font-mono text-[10px] uppercase tracking-widest mb-1">Critical</div>
            <div className="text-red-400 font-mono text-2xl font-bold">
              {audit ? audit.flagged_users.reduce((acc, u) => acc + (u.summary?.critical || 0), 0) : '—'}
            </div>
          </div>
          <div className="border border-[#2A0F3D] bg-[#0C0612] p-4">
            <div className="text-[#6B8E73] font-mono text-[10px] uppercase tracking-widest mb-1">Warning</div>
            <div className="text-amber-400 font-mono text-2xl font-bold">
              {audit ? audit.flagged_users.reduce((acc, u) => acc + (u.summary?.warning || 0), 0) : '—'}
            </div>
          </div>
        </div>

        {/* Filter chips */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {['all', 'critical', 'warning'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider border ${
                filter === f
                  ? 'bg-[#39FF14] text-black border-[#39FF14]'
                  : 'text-[#A3C7A8] border-[#2A0F3D] hover:border-[#39FF14] hover:text-[#39FF14]'
              }`}
              data-testid={`dq-filter-${f}`}
            >
              {f}
            </button>
          ))}
          <Button
            onClick={fetchAudit}
            disabled={loading}
            variant="ghost"
            className="ml-auto text-[#A3C7A8] hover:text-[#39FF14] font-mono text-xs uppercase tracking-wider"
            data-testid="dq-refresh-button"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Region + role filter selects */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div className="flex items-center gap-1.5">
            <Label className="text-[#6B8E73] font-mono text-[10px] uppercase tracking-widest">// Region</Label>
            <select
              value={regionFilter}
              onChange={(ev) => setRegionFilter(ev.target.value)}
              className="bg-[#050003] border border-[#2A0F3D] text-[#39FF14] font-mono text-xs uppercase tracking-wider px-2 py-1.5 rounded-none focus:outline-none focus:border-[#39FF14]"
              data-testid="dq-filter-region"
            >
              <option value="all">all regions</option>
              {regions.map((r) => (
                <option key={r} value={r}>
                  {r === '__none__' ? '— no region —' : r}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <Label className="text-[#6B8E73] font-mono text-[10px] uppercase tracking-widest">// Role</Label>
            <select
              value={roleFilter}
              onChange={(ev) => setRoleFilter(ev.target.value)}
              className="bg-[#050003] border border-[#2A0F3D] text-[#39FF14] font-mono text-xs uppercase tracking-wider px-2 py-1.5 rounded-none focus:outline-none focus:border-[#39FF14]"
              data-testid="dq-filter-role"
            >
              <option value="all">all roles</option>
              {roles.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          {(regionFilter !== 'all' || roleFilter !== 'all') && (
            <button
              type="button"
              onClick={() => { setRegionFilter('all'); setRoleFilter('all'); }}
              className="text-[#A3C7A8] hover:text-[#39FF14] font-mono text-[10px] uppercase tracking-wider underline"
              data-testid="dq-filter-clear"
            >
              clear filters
            </button>
          )}
          <span className="ml-auto text-[#6B8E73] font-mono text-[10px] uppercase tracking-widest" data-testid="dq-row-count">
            {rows.length} shown
          </span>
        </div>

        {/* Rows */}
        {rows.length === 0 && !loading && (
          <div className="border border-[#2A0F3D] bg-[#0C0612] p-8 text-center">
            <CheckCircle2 className="w-10 h-10 text-[#39FF14] mx-auto mb-2" />
            <p className="text-[#A3C7A8] font-mono text-sm">All clear — no flagged records.</p>
          </div>
        )}

        <div className="space-y-2">
          {rows.map((u) => {
            const isOpen = expanded === u.id;
            const e = editing[u.id] || {};
            return (
              <div key={u.id} className="border border-[#2A0F3D] bg-[#0C0612]" data-testid={`dq-row-${u.id}`}>
                <div className="w-full p-4 flex items-center justify-between gap-3 hover:bg-[#111A14] transition-colors">
                  <Link
                    to={`/dispatch/admin/users?openUser=${u.id}&from=data-quality`}
                    className="flex-1 min-w-0 group focus:outline-none focus:ring-1 focus:ring-[#39FF14]"
                    data-testid={`dq-row-open-user-${u.id}`}
                    title="Open in customer management"
                  >
                    <div className="text-[#39FF14] font-mono text-sm font-bold truncate flex items-center gap-1.5 group-hover:underline">
                      {u.name || u.email}
                      <ArrowUpRight className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100 shrink-0" />
                    </div>
                    <div className="text-[#A3C7A8] font-mono text-xs truncate">
                      {u.email} · {u.region || 'no region'} · <span className="uppercase">{u.role || 'customer'}</span>
                    </div>
                  </Link>
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : u.id)}
                    className="flex items-center gap-2 shrink-0 pl-2 border-l border-[#2A0F3D] focus:outline-none focus:ring-1 focus:ring-[#39FF14]"
                    data-testid={`dq-row-toggle-${u.id}`}
                    aria-expanded={isOpen}
                    aria-label={isOpen ? 'Collapse issues' : 'Expand issues'}
                  >
                    <SeverityBadge sev={u.highest_severity} />
                    <span className="font-mono text-xs text-[#A3C7A8]">{u.summary?.total || 0} issue(s)</span>
                    <ChevronDown
                      className={`w-4 h-4 text-[#A3C7A8] transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                </div>

                {isOpen && (
                  <div className="border-t border-[#2A0F3D] p-4 space-y-4">
                    <div>
                      <Label className="text-[#6B8E73] font-mono text-[10px] uppercase tracking-widest">// Issues</Label>
                      <ul className="mt-2 space-y-1">
                        {u.issues.map((iss, i) => (
                          <li key={i} className="text-sm font-mono flex items-start gap-2">
                            <SeverityBadge sev={iss.severity} />
                            <span className={iss.severity === 'critical' ? 'text-red-400' : 'text-amber-400'}>
                              {iss.message}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-[#6B8E73] font-mono text-[10px] uppercase tracking-widest">Name</Label>
                        <Input
                          defaultValue={u.name || ''}
                          onChange={(ev) => setEditing((p) => ({ ...p, [u.id]: { ...e, name: ev.target.value } }))}
                          className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none"
                          data-testid={`dq-edit-name-${u.id}`}
                        />
                      </div>
                      <div>
                        <Label className="text-[#6B8E73] font-mono text-[10px] uppercase tracking-widest">Phone</Label>
                        <Input
                          defaultValue={u.phone || ''}
                          onChange={(ev) => setEditing((p) => ({ ...p, [u.id]: { ...e, phone: ev.target.value } }))}
                          className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none"
                          data-testid={`dq-edit-phone-${u.id}`}
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-[#6B8E73] font-mono text-[10px] uppercase tracking-widest">
                        Address (pick from dropdown to fix structured fields)
                      </Label>
                      <div className="mt-2">
                        <AddressAutocomplete
                          value={e.address ?? null}
                          onChange={(addr) => setEditing((p) => ({ ...p, [u.id]: { ...e, address: addr } }))}
                          placeholder="Re-pick address..."
                          data-testid={`dq-edit-address-${u.id}`}
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-2 pt-2 flex-wrap">
                      <Button
                        onClick={() => handleSaveFix(u.id)}
                        disabled={Object.keys(e).length === 0}
                        className="bg-[#39FF14] text-black hover:bg-[#2bcc10] font-mono text-xs uppercase tracking-wider rounded-none"
                        data-testid={`dq-save-${u.id}`}
                      >
                        Save fix
                      </Button>
                      <Button
                        onClick={() => handleRecheck(u.id)}
                        variant="ghost"
                        className="text-[#A3C7A8] hover:text-[#39FF14] font-mono text-xs uppercase tracking-wider"
                        data-testid={`dq-recheck-${u.id}`}
                      >
                        Re-check only
                      </Button>
                      {/* Feb 2026 — Ask the customer to fix it themselves. */}
                      {(u.role || 'customer') === 'customer' && (
                        <>
                          <span className="text-[#3A2F45] font-mono text-xs">|</span>
                          <Button
                            onClick={() => handleNotify(u.id, ['email'])}
                            disabled={!!notifyingId}
                            variant="outline"
                            className="border-[#FF9F0A] text-[#FF9F0A] hover:bg-[#FF9F0A] hover:text-black font-mono text-xs uppercase tracking-wider rounded-none disabled:opacity-40"
                            data-testid={`dq-notify-email-${u.id}`}
                          >
                            <Mail className="w-3 h-3 mr-1" />
                            {notifyingId === `${u.id}:email` ? 'Sending...' : 'Email fix link'}
                          </Button>
                          <Button
                            onClick={() => handleNotify(u.id, ['push'])}
                            disabled={!!notifyingId}
                            variant="outline"
                            className="border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black font-mono text-xs uppercase tracking-wider rounded-none disabled:opacity-40"
                            data-testid={`dq-notify-push-${u.id}`}
                          >
                            <Bell className="w-3 h-3 mr-1" />
                            {notifyingId === `${u.id}:push` ? 'Sending...' : 'Push'}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Bulk-notify confirmation modal */}
      {bulkConfirmOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setBulkConfirmOpen(false)}
          data-testid="dq-bulk-confirm-overlay"
        >
          <div
            className="border-2 border-[#FF9F0A] bg-[#0C0612] p-6 max-w-md w-full"
            onClick={(ev) => ev.stopPropagation()}
          >
            <h3 className="text-[#FF9F0A] font-mono text-base font-bold uppercase tracking-wider mb-2">
              ⚠ Confirm bulk notify
            </h3>
            <p className="text-[#A3C7A8] font-mono text-xs mb-4">
              // This will send the &apos;fix your profile&apos; template to every flagged
              CUSTOMER. Staff are excluded. 24h dedup prevents same-email double-sends.
            </p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => handleBulkNotify(['email'])}
                className="bg-[#FF9F0A] text-black hover:bg-[#cc7e08] font-mono text-xs uppercase tracking-wider rounded-none"
                data-testid="dq-bulk-confirm-email"
              >
                <Mail className="w-3 h-3 mr-1" /> Send EMAIL to all flagged
              </Button>
              <Button
                onClick={() => handleBulkNotify(['push'])}
                className="bg-[#00F0FF] text-black hover:bg-[#00C8D5] font-mono text-xs uppercase tracking-wider rounded-none"
                data-testid="dq-bulk-confirm-push"
              >
                <Bell className="w-3 h-3 mr-1" /> Send PUSH to all flagged
              </Button>
              <Button
                onClick={() => handleBulkNotify(['email', 'push'])}
                className="bg-[#39FF14] text-black hover:bg-[#2bcc10] font-mono text-xs uppercase tracking-wider rounded-none"
                data-testid="dq-bulk-confirm-both"
              >
                <Send className="w-3 h-3 mr-1" /> Send BOTH to all flagged
              </Button>
              <Button
                onClick={() => setBulkConfirmOpen(false)}
                variant="ghost"
                className="text-[#A3C7A8] hover:text-[#39FF14] font-mono text-xs uppercase tracking-wider mt-1"
                data-testid="dq-bulk-confirm-cancel"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
